/**
 * BookCoverPicker — optional book-cover uploader with drag-to-reposition
 * and pinch/wheel/slider zoom. Used inside the Post-a-Project and Edit-a-
 * Project forms. Kept as a self-contained component so nothing else in
 * the codebase needs to know about cover uploads.
 *
 * Contract:
 *   value: {
 *     url: string,            // "/api/uploads/<path>" or "" for no cover
 *     position: { x: number, y: number, zoom: number }  // percentages
 *   }
 *   onChange(next) — called with the merged next value on any edit.
 *
 * Positioning model: the preview is a fixed 2:3 (portrait book) aspect
 * container. The cover is drawn as a background-image with
 *   background-size: <zoom>% <zoom>%
 *   background-position: <x>% <y>%
 * so the same values can be re-used at display time on the job card /
 * detail page. Values are stored on the job as `cover_position`.
 *
 * Interactions:
 *   - Mouse drag on the preview → shifts x/y in percentages.
 *   - Single-finger touch drag → same on touchscreens.
 *   - Zoom slider (100–250%) → sets zoom.
 *   - Mouse wheel over the preview → adjusts zoom in ±5% steps.
 *   - "Replace image" and "Remove" buttons for later edits.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { ImagePlus, Move, Trash2, ZoomIn } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

const DEFAULT_POS = { x: 50, y: 50, zoom: 100 };
const MAX_ZOOM = 250;
const MIN_ZOOM = 100;
const ACCEPT = "image/jpeg,image/png,image/webp,image/gif";
const MAX_BYTES = 8 * 1024 * 1024;

export function BookCoverPicker({ value, onChange, disabled = false }) {
  const url = value?.url || "";
  const position = { ...DEFAULT_POS, ...(value?.position || {}) };
  const [uploading, setUploading] = useState(false);
  const dragState = useRef(null);
  const previewRef = useRef(null);

  const update = useCallback((patch) => {
    onChange({
      url,
      position: { ...position, ...patch },
      ...(patch.url !== undefined ? { url: patch.url } : {}),
    });
  }, [onChange, url, position]);

  const setUrl = useCallback((nextUrl, resetPos = true) => {
    onChange({
      url: nextUrl,
      position: resetPos ? { ...DEFAULT_POS } : position,
    });
  }, [onChange, position]);

  const handleFile = async (file) => {
    if (!file) return;
    if (!ACCEPT.split(",").includes(file.type)) {
      toast.error("Please pick a JPG, PNG, WEBP or GIF image.");
      return;
    }
    if (file.size > MAX_BYTES) {
      toast.error("Cover image is too large (max 8 MB).");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setUrl(data.url, true);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setUploading(false);
    }
  };

  const onFileInput = (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    handleFile(f);
  };

  // Drag-to-reposition (mouse + touch).
  const beginDrag = (clientX, clientY) => {
    if (!url || disabled) return;
    const rect = previewRef.current?.getBoundingClientRect();
    if (!rect) return;
    dragState.current = {
      startClientX: clientX,
      startClientY: clientY,
      startX: position.x,
      startY: position.y,
      width: rect.width,
      height: rect.height,
    };
  };
  const moveDrag = (clientX, clientY) => {
    const s = dragState.current;
    if (!s) return;
    // Drag distance in preview-percent. Since the image is zoomed by
    // `zoom%`, one pixel of visual movement corresponds to
    //   (100 / (zoom - 100)) percent
    // of background-position — but for simplicity and predictable feel
    // we translate 1px → (100 / rect) percent and let higher zoom feel
    // "faster". That matches native mobile photo-crop behaviour.
    const dx = ((clientX - s.startClientX) / s.width) * 100;
    const dy = ((clientY - s.startClientY) / s.height) * 100;
    const nx = clamp(s.startX - dx, 0, 100);
    const ny = clamp(s.startY - dy, 0, 100);
    update({ x: nx, y: ny });
  };
  const endDrag = () => { dragState.current = null; };

  useEffect(() => {
    const onMove = (e) => moveDrag(e.clientX, e.clientY);
    const onUp = () => endDrag();
    const onTouchMove = (e) => {
      if (!dragState.current) return;
      const t = e.touches[0];
      if (!t) return;
      // Prevent the page from scrolling while the user is repositioning.
      if (e.cancelable) e.preventDefault();
      moveDrag(t.clientX, t.clientY);
    };
    const onTouchEnd = () => endDrag();
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    window.addEventListener("touchmove", onTouchMove, { passive: false });
    window.addEventListener("touchend", onTouchEnd);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      window.removeEventListener("touchmove", onTouchMove);
      window.removeEventListener("touchend", onTouchEnd);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url, position.zoom]);

  const onWheel = (e) => {
    if (!url || disabled) return;
    e.preventDefault();
    const dz = e.deltaY < 0 ? 5 : -5;
    update({ zoom: clamp(position.zoom + dz, MIN_ZOOM, MAX_ZOOM) });
  };

  const bgStyle = url
    ? {
        backgroundImage: `url(${url})`,
        backgroundSize: `${position.zoom}% ${position.zoom}%`,
        backgroundPosition: `${position.x}% ${position.y}%`,
        backgroundRepeat: "no-repeat",
      }
    : undefined;

  return (
    <div className="space-y-3" data-testid="book-cover-picker">
      <div className="flex flex-col sm:flex-row gap-4 sm:items-start">
        {/* Portrait 2:3 preview */}
        <div
          ref={previewRef}
          className={`relative shrink-0 w-40 aspect-[2/3] rounded-lg overflow-hidden border-2 ${url ? "border-[hsl(var(--sb-gold))]/50" : "border-dashed border-border"} bg-muted select-none ${url && !disabled ? "cursor-grab active:cursor-grabbing" : ""}`}
          style={bgStyle}
          onMouseDown={(e) => { e.preventDefault(); beginDrag(e.clientX, e.clientY); }}
          onTouchStart={(e) => { const t = e.touches[0]; if (t) beginDrag(t.clientX, t.clientY); }}
          onWheel={onWheel}
          data-testid="book-cover-preview"
        >
          {!url && (
            <div className="absolute inset-0 flex flex-col items-center justify-center text-center text-xs text-muted-foreground px-3">
              <ImagePlus size={22} className="mb-1 opacity-70" />
              <span>No cover yet</span>
              <span className="opacity-70 mt-0.5">Optional</span>
            </div>
          )}
          {url && (
            <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/55 to-transparent px-2 py-1.5 text-[10px] uppercase tracking-[0.12em] text-white/90 flex items-center gap-1 pointer-events-none">
              <Move size={11} /> Drag to reposition
            </div>
          )}
        </div>

        <div className="min-w-0 flex-1 space-y-3">
          <div className="flex flex-wrap gap-2">
            <label className={`btn-ghost border cursor-pointer whitespace-nowrap ${disabled ? "opacity-60 pointer-events-none" : ""}`} data-testid="book-cover-choose-btn">
              <ImagePlus size={14} className="mr-1.5" />
              {url ? "Replace image" : "Upload cover"}
              <input
                type="file"
                hidden
                accept={ACCEPT}
                onChange={onFileInput}
                data-testid="book-cover-file-input"
                disabled={disabled || uploading}
              />
            </label>
            {url && !disabled && (
              <button
                type="button"
                className="btn-ghost border text-[hsl(var(--sb-clay))] whitespace-nowrap"
                onClick={() => setUrl("", true)}
                data-testid="book-cover-remove-btn"
              >
                <Trash2 size={14} className="mr-1.5" />
                Remove
              </button>
            )}
            {uploading && (
              <span className="text-xs text-muted-foreground self-center">Uploading…</span>
            )}
          </div>

          {url && (
            <div className="space-y-2" data-testid="book-cover-zoom-block">
              <div className="flex items-center gap-2 text-xs">
                <ZoomIn size={14} className="text-muted-foreground" />
                <span className="text-muted-foreground">Zoom</span>
                <span className="ml-auto tabular-nums">{Math.round(position.zoom)}%</span>
              </div>
              <input
                type="range"
                min={MIN_ZOOM}
                max={MAX_ZOOM}
                step={1}
                value={position.zoom}
                onChange={(e) => update({ zoom: Number(e.target.value) })}
                className="w-full accent-[hsl(var(--sb-forest))]"
                data-testid="book-cover-zoom-slider"
                disabled={disabled}
              />
            </div>
          )}

          <p className="text-xs text-muted-foreground leading-relaxed">
            Optional. JPG, PNG, WEBP or GIF up to 8 MB. Drag the preview
            {" "}or use the zoom slider so the important part of your cover
            stays visible when the project is listed.
          </p>
        </div>
      </div>
    </div>
  );
}

function clamp(n, lo, hi) {
  return Math.max(lo, Math.min(hi, n));
}
